import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';

const MESSAGE = 'Du har ändringar som inte är sparade. Lämnar du nu försvinner de.';

interface UnsavedChanges {
  setDirty: (dirty: boolean) => void;
  /** Returnerar true om det är okej att fortsätta och kasta utkastet. */
  confirmLeave: () => boolean;
}

const Context = createContext<UnsavedChanges | null>(null);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const dirtyRef = useRef(false);

  useEffect(() => {
    function onBeforeUnload(event: BeforeUnloadEvent) {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const value = useMemo<UnsavedChanges>(
    () => ({
      setDirty: (dirty) => {
        dirtyRef.current = dirty;
      },
      confirmLeave: () => {
        if (!dirtyRef.current) return true;
        const proceed = window.confirm(MESSAGE);
        if (proceed) dirtyRef.current = false;
        return proceed;
      }
    }),
    []
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useUnsavedChanges(): UnsavedChanges {
  const value = useContext(Context);
  if (!value) {
    throw new Error('UnsavedChangesProvider saknas.');
  }
  return value;
}

/** Håller den globala flaggan i synk med panelens utkast. */
export function useDirty(isDirty: boolean): void {
  const { setDirty } = useUnsavedChanges();

  useEffect(() => {
    setDirty(isDirty);
    return () => setDirty(false);
  }, [isDirty, setDirty]);
}
